import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import CoinIcon from '../components/CoinIcon'
import Spinner from '../components/Spinner'
import TradingViewAlerts from '../components/calls/TradingViewAlerts'
import useLockACall from '../hooks/useLockACall'
import { LOCK_CALL_PATH, callPath, creatorRecordPath } from '../routes'
import { callsAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { TradingSignal } from '../types'
import { displaySymbol } from '../utils/displaySymbol'
import { formatPrice } from '../utils/marketPrice'
import { formatUtcSeconds } from '../utils/utcTime'

const outcome = (call: TradingSignal): { label: string; tone: string } => {
  if (call.status === 'active' || call.status === 'pending') return { label: 'Open', tone: 'text-accent' }
  if (call.status === 'cancelled') return { label: 'Cancelled', tone: 'text-gray-400' }
  if (call.win === true) return { label: 'Win', tone: 'text-green-300' }
  if (call.win === false) return { label: 'Loss', tone: 'text-red-300' }
  return { label: 'Break-even', tone: 'text-gray-300' }
}

/** /calls: the user's locked calls, open first, and the way to lock another. */
const MyCalls = () => {
  const options = useLockACall()
  const handle = useAuthStore((state) => state.user?.username)
  const [calls, setCalls] = useState<TradingSignal[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!options) return undefined
    let live = true
    callsAPI
      .mine()
      .then((rows) => live && setCalls(rows))
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [options])

  if (options === undefined)
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  if (options === null) return <Navigate to="/" replace />

  const openCount = calls?.filter((c) => c.status === 'active').length ?? 0
  const full = openCount >= options.max_open_calls

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50">My calls</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Locked at the live price, tracked every 5 minutes, scored in R. Public and permanent.
          </p>
          {handle && calls !== null && calls.length > 0 && (
            <Link
              to={creatorRecordPath(handle)}
              className="mt-1 inline-block text-sm text-accent hover:underline"
              data-testid="my-record-link"
            >
              Your public record →
            </Link>
          )}
        </div>
        {full ? (
          <p className="text-sm text-gray-400" data-testid="calls-full">
            {options.max_open_calls} open calls: lock another when one closes.
          </p>
        ) : (
          <Link
            to={LOCK_CALL_PATH}
            className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-terminal-bg hover:bg-accent-hover"
          >
            Lock a call
          </Link>
        )}
      </div>

      {calls === null && !failed && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}
      {failed && <p className="text-sm text-red-300">Your calls could not be loaded. Try again in a minute.</p>}
      {calls !== null && calls.length === 0 && (
        <p className="rounded-xl border border-terminal-border bg-terminal-surface p-6 text-sm text-gray-300" data-testid="no-calls">
          No calls yet. Lock one and it gets a public receipt you can share.
        </p>
      )}
      {calls !== null && calls.length > 0 && (
        <ul className="divide-y divide-terminal-border rounded-xl border border-terminal-border bg-terminal-surface" data-testid="my-calls">
          {calls.map((call) => {
            const result = outcome(call)
            return (
              <li key={call.id}>
                <Link
                  to={callPath(call.id)}
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 hover:bg-white/5"
                  data-testid="my-call"
                >
                  <CoinIcon symbol={call.symbol} size={24} />
                  <span className="font-semibold text-gray-100">{displaySymbol(call.symbol)}</span>
                  <span className={call.signal_type === 'long' ? 'text-green-300' : 'text-red-300'}>
                    {call.signal_type === 'long' ? 'Long' : 'Short'}
                  </span>
                  <span className="font-mono text-sm text-gray-300">@ {formatPrice(call.entry_price)}</span>
                  <span className={`text-sm font-medium ${result.tone}`}>{result.label}</span>
                  {call.profit_loss_percent != null && call.status === 'closed' && (
                    <span className="font-mono text-sm text-gray-300">
                      {call.profit_loss_percent >= 0 ? '+' : ''}
                      {call.profit_loss_percent.toFixed(2)}%
                    </span>
                  )}
                  <span className="ml-auto text-xs text-gray-500">
                    #{call.id} · {call.created_at ? formatUtcSeconds(call.created_at) : ''}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}

      <TradingViewAlerts />
    </div>
  )
}

export default MyCalls
